import { useCallback, useEffect, useState, Suspense, lazy } from 'react';
import { Toaster } from 'react-hot-toast';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LiteModeProvider, useLiteMode } from './hooks/useLiteMode';
import { OpenInTelegramScreen } from './components/OpenInTelegramScreen';
import { SubscribeScreen } from './components/SubscribeScreen';
import { AnimatedBackground } from './components/ui/animated-background';
import { isTelegramAvailable } from './utils/telegramAdapter';
import { I18nProvider } from './i18n';
import { useI18n } from './i18n';

const MainAppPage = lazy(() => import('./pages/MainAppPage').then(m => ({ default: m.MainAppPage })));

const TELEGRAM_REQUIRED = import.meta.env.VITE_TELEGRAM_REQUIRED === 'true';

const QuickLoader = () => (
  <div className="loading-overlay">
    <div className="quick-spinner" />
  </div>
);

type AccessState = 'loading' | 'need_bot' | 'need_subscription' | 'subscription_error' | 'allowed';

const SubscriptionErrorScreen = ({ message, onRetry }: { message: string; onRetry: () => void }) => {
  const { t } = useI18n();
  return <div className="min-h-dvh bg-sheriff-bg flex items-center justify-center p-6">
    <div className="max-w-md w-full rounded-3xl border border-white/[0.08] bg-gradient-to-br from-[#0E0E0E] to-[#141414] p-8 text-center shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
      <h1 className="text-2xl font-semibold text-white/95 mb-3">{t('accessErrorTitle')}</h1>
      <p className="text-white/65 text-base leading-relaxed mb-6">{message}</p>
      <button
        onClick={onRetry}
        className="w-full py-3.5 px-6 rounded-2xl bg-gradient-to-br from-[#0E0E0E] to-[#141414] text-white/95 text-base font-medium border border-white/[0.08] transition-all duration-200 hover:scale-[1.01] active:scale-[0.98]"
      >
        {t('accessRetry')}
      </button>
    </div>
  </div>;
};

function AppContent() {
  const { isLiteMode, isMobile } = useLiteMode();
  const { t } = useI18n();
  const [accessState, setAccessState] = useState<AccessState>('loading');
  const [accessError, setAccessError] = useState('');

  const checkAccess = useCallback(async () => {
      if (typeof window === 'undefined') {
        setAccessState('loading');
        return;
      }

      setAccessState('loading');
      setAccessError(t('accessDefaultError'));

      const initData = window.Telegram?.WebApp?.initData || '';

      if (!initData) {
        setAccessState('allowed');
        return;
      }

      try {
        const response = await fetch('/api/auth/subscription', {
          headers: {
            'X-Telegram-InitData': initData,
          },
        });

        if (response.ok) {
          setAccessState('allowed');
          return;
        }

        let code = '';
        let message = '';
        try {
          const payload = await response.json();
          const detail = payload?.detail || payload;
          code = detail?.code || detail?.error || '';
          message = detail?.message || '';
        } catch {
          code = '';
        }

        if (response.status === 403 && code === 'SUBSCRIPTION_REQUIRED') {
          setAccessState('need_subscription');
        } else {
          setAccessError(message || t('accessTelegramError'));
          setAccessState('subscription_error');
        }
      } catch (error) {
        console.error('Access check failed:', error);
        setAccessError(t('accessServiceError'));
        setAccessState('subscription_error');
      }
  }, [t]);

  useEffect(() => {
    checkAccess();
  }, [checkAccess]);

  useEffect(() => {
    if (isLiteMode) {
      document.body.classList.add('lite-mode');
    } else {
      document.body.classList.remove('lite-mode');
    }
    return () => {
      document.body.classList.remove('lite-mode');
    };
  }, [isLiteMode]);

  const showBrowserBadge = !isTelegramAvailable() && !TELEGRAM_REQUIRED;

  const particleCount = isLiteMode ? 0 : (isMobile ? 12 : 30);

  return (
    <>
      {showBrowserBadge && (
        <div className="fixed top-3 right-3 z-[60] rounded-full border border-[#D4AF37]/25 bg-black/75 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#D4AF37] shadow-[0_8px_24px_rgba(0,0,0,0.35)] backdrop-blur">
          {t('devMode')}
        </div>
      )}

      {accessState === 'loading' ? (
        <QuickLoader />
      ) : accessState === 'need_bot' ? (
        <OpenInTelegramScreen />
      ) : accessState === 'need_subscription' ? (
        <SubscribeScreen onVerified={() => setAccessState('allowed')} />
      ) : accessState === 'subscription_error' ? (
        <SubscriptionErrorScreen message={accessError} onRetry={checkAccess} />
      ) : (
        <div className="relative min-h-dvh bg-sheriff-bg flex flex-col">
          {!isLiteMode && (
            <div className="fixed inset-0 z-0 pointer-events-none" style={{ touchAction: 'none' }}>
              <AnimatedBackground 
                color="#d4af37" 
                particleCount={particleCount}
                className="opacity-50"
              />
            </div>
          )}

          <div className="relative z-10 flex-1 flex flex-col min-h-0">
            <Suspense fallback={<QuickLoader />}>
              <MainAppPage />
            </Suspense>
          </div>
          
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 3000,
              style: {
                background: 'rgba(20, 20, 22, 0.85)',
                color: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '16px',
                padding: '14px 18px',
                fontSize: '15px',
                fontWeight: '500',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 8px 24px rgba(0, 0, 0, 0.5)',
                maxWidth: '90vw',
              },
              success: {
                iconTheme: {
                  primary: '#D4AF37',
                  secondary: 'rgba(20, 20, 22, 0.95)',
                },
                style: {
                  border: '1px solid rgba(212, 175, 55, 0.15)',
                },
              },
              error: {
                iconTheme: {
                  primary: '#FF6B6B',
                  secondary: 'rgba(20, 20, 22, 0.95)',
                },
                style: {
                  border: '1px solid rgba(255, 107, 107, 0.15)',
                },
              },
            }}
          />
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <LiteModeProvider>
      <I18nProvider>
        <ErrorBoundary><AppContent /></ErrorBoundary>
      </I18nProvider>
    </LiteModeProvider>
  );
}

export default App;

